import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";

// Importación de la imagen 9:16
import firmaImg from "../static/media/firma_ee.webp";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const planes = [
  { tiempo: "7 días", precio: "$10,00" },
  { tiempo: "30 días", precio: "$12,00" },
  { tiempo: "1 año", precio: "$17,99", recomendado: true },
  { tiempo: "2 años", precio: "$29,99" },
  { tiempo: "3 años", precio: "$39,99" },
  { tiempo: "4 años", precio: "$49,99" },
  { tiempo: "5 años", precio: "$59,99" },
];

const beneficios = [
  "Asesoría en el proceso de solicitud.",
  "Emisión rápida y segura.",
  "Guía básica para descarga y uso.",
];

export default function FirmaElectronica() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [planSeleccionado, setPlanSeleccionado] = useState(2); // Por defecto "1 año"

  // URL base para WhatsApp
  const whatsappUrl = `https://api.whatsapp.com/send/?phone=593985092679&text=${encodeURIComponent(
    `Hola, me interesa adquirir la Firma Electrónica por ${planes[planSeleccionado].tiempo} a ${planes[planSeleccionado].precio}.`,
  )}`;

  return (
    <Box
      component="section"
      id="firma-electronica"
      sx={{
        py: { xs: 4, md: 5 }, // Padding reducido para encajar en una pantalla
        bgcolor: "#F8FAFF",
        position: "relative",
        overflow: "hidden",
        minHeight: { md: "85vh" }, // Asegura que tome gran parte de la pantalla sin desbordar
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* ─── DECORACIONES GEOMÉTRICAS (Glow) ─── */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "-10%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
          {/* ─── COLUMNA IZQUIERDA: IMAGEN 9:16 ANIMADA ─── */}
          <Grid item xs={12} md={4} lg={4.5}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%", // Obliga a la caja a igualar la altura de la columna derecha
                minHeight: { xs: "400px", md: "100%" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Resplandor de fondo */}
              <MotionBox
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.4, 0.7, 0.4],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                sx={{
                  position: "absolute",
                  width: "85%",
                  height: "85%",
                  background:
                    "linear-gradient(135deg, rgba(44,95,190,0.3) 0%, rgba(14,165,233,0.3) 100%)",
                  filter: "blur(35px)",
                  borderRadius: "20px",
                  zIndex: 0,
                }}
              />

              {/* Imagen 9:16 */}
              <MotionBox
                animate={{ y: [-8, 8, -8] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: "90%",
                  height: "90%",
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 20px 40px rgba(15,23,42,0.12)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  bgcolor: "#fff",
                }}
              >
                <Box
                  component="img"
                  src={firmaImg}
                  alt="Firma Electrónica Ecuador"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // Esto garantiza que llene el espacio sin deformarse
                    objectPosition: "center",
                  }}
                />
              </MotionBox>
            </Box>
          </Grid>

          {/* ─── COLUMNA DERECHA: INFORMACIÓN Y PRECIOS ─── */}
          <Grid
            item
            xs={12}
            md={8}
            lg={7.5}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center", // Centra el contenido verticalmente
            }}
          >
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                component="h2"
                sx={{
                  fontWeight: 750,
                  fontSize: { xs: "1.8rem", md: "2.4rem" },
                  color: "#1A2B4A",
                  lineHeight: 1.1,
                  mb: 1.5,
                }}
              >
                Firma Electrónica <br />
                <Box component="span" sx={{ color: "#0EA5E9" }}>
                  desde $10.00
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: "#475569",
                  fontSize: "0.95rem",
                  lineHeight: 1.5,
                  mb: 2.5,
                }}
              >
                Válida para trámites digitales en Ecuador. Ideal para servidores
                públicos, profesionales y usuarios que necesitan firmar
                documentos en plataformas como{" "}
                <strong>QUIPUX, CHASQUI, SISDOM, SRI, FirmaEC</strong> y
                sistemas compatibles.
              </Typography>

              {/* Lista de beneficios en una sola línea o grid compacta si es posible */}
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {beneficios.map((texto, i) => (
                  <Grid item xs={12} sm={12} key={i}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "5px",
                          bgcolor: "#2C5FBE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M2 5l2.5 2.5L8 3"
                            stroke="white"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>
                      <Typography
                        sx={{
                          color: "#334155",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        {texto}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Cuadrícula interactiva de Planes (Compacta) */}
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#1A2B4A",
                  mb: 1.5,
                  fontSize: "0.95rem",
                }}
              >
                Selecciona la vigencia:
              </Typography>

              <Grid container spacing={1.2} sx={{ mb: 3 }}>
                {planes.map((plan, index) => {
                  const isActive = planSeleccionado === index;
                  return (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <MotionBox
                        onClick={() => setPlanSeleccionado(index)}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        sx={{
                          p: 1, // Reducido para ahorrar espacio vertical
                          borderRadius: "10px",
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: isActive
                            ? "#2C5FBE"
                            : "rgba(44,95,190,0.15)",
                          bgcolor: isActive ? "#2C5FBE" : "#fff",
                          textAlign: "center",
                          position: "relative",
                          overflow: "hidden",
                          boxShadow: isActive
                            ? "0 6px 15px rgba(44,95,190,0.2)"
                            : "none",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {plan.recomendado && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: "50%",
                              transform: "translateX(-50%)",
                              bgcolor: "#0EA5E9",
                              color: "#fff",
                              fontSize: "0.45rem",
                              fontWeight: 75,
                              px: 0.8,
                              py: 0.2,
                              borderBottomRadius: "4px",
                              letterSpacing: "0.05em",
                            }}
                          >
                            RECOMENDADO
                          </Box>
                        )}
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: isActive
                              ? "rgba(255,255,255,0.8)"
                              : "#64748B",
                            mt: plan.recomendado ? 0.8 : 0,
                          }}
                        >
                          {plan.tiempo}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 750,
                            color: isActive ? "#fff" : "#1A2B4A",
                          }}
                        >
                          {plan.precio}
                        </Typography>
                      </MotionBox>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Botón CTA Dinámico */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <MotionButton
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  fullWidth={isMobile}
                  sx={{
                    background:
                      "linear-gradient(135deg,#2C5FBE 0%,#1A3D8F 100%)",
                    borderRadius: "10px",
                    px: { xs: 3, md: 4 },
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 750,
                    fontSize: "0.95rem",
                    color: "#fff",
                    boxShadow: "0 8px 20px rgba(44,95,190,0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#214A96 0%,#162E6B 100%)",
                    },
                  }}
                >
                  Solicitar por {planes[planSeleccionado].precio}
                </MotionButton>

                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#94A3B8",
                    fontWeight: 500,
                    textAlign: { xs: "center", md: "left" },
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  Servicio rápido, seguro y confiable. <br />
                  <a
                    href="https://www.drsolucionesinformaticas.com"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#0EA5E9", textDecoration: "none" }}
                  >
                    www.drsolucionesinformaticas.com
                  </a>
                </Typography>
              </Box>
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
